// UpdateAuthMethodModal.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getApiUrl } from "../../config";
import { getToken } from "../../helpers/tokenManager";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Flex, Input, Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import { isValidEmail } from "../../helpers/validation";

const UpdateAuthMethodModal: React.FC = () => {
  // From your modal provider:
  const { isUpdateAuthModalVisible, showErrorMessage, showVerifyEmailModal, closeUpdateAuthModal } =
    useModalState();
  // From your global state (for user info & refresh):
  const { info } = useGlobalState();

  // Local form states:
  const [newEmail, setNewEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Always call useEffect, but conditionally do setup inside it
  useEffect(() => {
    // If modal not visible, just return
    if (!isUpdateAuthModalVisible) {
      return;
    }
    // When the modal becomes visible, reset fields
    setNewEmail(info?.email || "");
  }, [info?.email, isUpdateAuthModalVisible]);

  const handleSubmit = async () => {
    if (process.env.NODE_ENV === 'development') console.log("Submitting new email:", newEmail);
    const bearerToken = getToken();
    if (!bearerToken) {
      showErrorMessage(`No bearer token found. Please sign in first.`);
      return;
    }

    // Validate before submitting. Without this a malformed address reaches the
    // mail send server-side and comes back as a generic 500 "Internal Server
    // Error" toast. The change-PASSWORD modal already validates client-side
    // ("New passwords do not match"), so this closes that inconsistency.
    const trimmedEmail = newEmail.trim();
    if (!isValidEmail(trimmedEmail)) {
      showErrorMessage("Please enter a valid email address.");
      return;
    }

    // Attempt to update email/password
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/request_change_email`,
        { email: trimmedEmail },
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
      if (process.env.NODE_ENV === 'development') console.log("Email updated successfully:", response.data);
    } catch (err: any) {
      // Fall back to a real sentence: interpolating a missing `message`
      // rendered the literal string "undefined" in the toast.
      return showErrorMessage(
        err.response?.data?.message || "Could not update your email. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }

    // Hand the address to VerifyEmailModal so its resend button has something
    // to resend to.
    showVerifyEmailModal(trimmedEmail);
    closeUpdateAuthModal();
  };

  return (
    <DialogRoot lazyMount open={isUpdateAuthModalVisible} onOpenChange={(e) => { if (!e.open) closeUpdateAuthModal(); }}>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isLoading) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Change email address</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Flex justifyContent="space-between" alignItems="baseline">
              <Text fontSize="sm" color="#52525b">Current email address:</Text>
              <Text fontSize="sm" fontWeight="medium" color="#18181b">{info?.email}</Text>
            </Flex>
            <Field label="New email address">
              <Input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                fontSize="sm"
              />
            </Field>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={closeUpdateAuthModal}>
            Cancel
          </Button>
          <Button colorPalette="purple" bg="#9333ea" onClick={handleSubmit} loading={isLoading}>
            Update email
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default UpdateAuthMethodModal;
